import { create } from "zustand";
import { combine, persist, createJSONStorage } from "zustand/middleware";
import { followsApi } from "@/lib/client/follows";
import { tokenService } from "@/lib/client/tokenService";
import { logError } from "@/lib/client/errors";

/** Cache duration for unsynced filters check (ms) */
const CACHE_DURATION = 5 * 60 * 1000; // 5 minutes

/**
 * State for tracking unsynced filters from follows.
 * Used to show prompts when followed brands/vendors have new filters.
 */
interface UnsyncedFiltersState {
  /** Whether there are unsynced filters */
  hasUnsynced: boolean;
  /** Count of unsynced brand filters */
  unsyncedBrandCount: number;
  /** Count of unsynced vendor filters */
  unsyncedVendorCount: number;
  /** Timestamp of last check */
  lastChecked: number | null;
  /** Whether a check is currently in progress */
  checking: boolean;
}

/**
 * Initial state for the follows store.
 * Contains followed brands/vendors, loading states, and unsynced filter tracking.
 */
const initialState = {
  /** Brands the user is following */
  followedBrands: [] as BrandFilter[],
  /** Vendors the user is following */
  followedVendors: [] as Vendor[],
  /** Loading state for follow operations */
  loading: false,
  /** Current authenticated user ID (for localStorage scoping) */
  currentUserId: null as string | null,
  /** Whether initial data load has completed */
  hasLoaded: false,
  /** State tracking unsynced filters from follows */
  unsyncedFilters: {
    hasUnsynced: false,
    unsyncedBrandCount: 0,
    unsyncedVendorCount: 0,
    lastChecked: null,
    checking: false,
  } as UnsyncedFiltersState,
};

// Safe localStorage wrapper
const isClient = typeof window !== "undefined";

/**
 * Safe localStorage wrapper that handles SSR and potential storage errors.
 * All operations are no-ops on server and log errors on failure.
 */
const safeLocalStorage = {
  getItem: (key: string): string | null => {
    if (!isClient) return null;
    try {
      return localStorage.getItem(key);
    } catch (error) {
      logError(error as Error, { context: "localStorage.getItem" });
      return null;
    }
  },
  setItem: (key: string, value: string): void => {
    if (!isClient) return;
    try {
      localStorage.setItem(key, value);
    } catch (error) {
      logError(error as Error, { context: "localStorage.setItem" });
    }
  },
  removeItem: (key: string): void => {
    if (!isClient) return;
    try {
      localStorage.removeItem(key);
    } catch (error) {
      logError(error as Error, { context: "localStorage.removeItem" });
    }
  },
};

/**
 * Helper to get user-scoped storage key.
 * Ensures each user's follows are stored separately in localStorage.
 *
 * @param userId - The user ID to scope the key for, or null for anonymous
 * @returns Storage key for the user's follows
 */
const getUserStorageKey = (userId: string | null) => {
  return userId ? `follows-storage-${userId}` : "follows-storage-anonymous";
};

/**
 * Follows store manages brand and vendor follows.
 *
 * Features:
 * - Follow/unfollow brands and vendors with optimistic UI updates
 * - Unsynced filters tracking (shows prompts when follows have new filters)
 * - User-scoped localStorage persistence
 * - Automatic cache invalidation on follow changes
 *
 * Persistence:
 * - Followed brands/vendors and unsynced filter cache are persisted to localStorage
 * - Storage is user-scoped to prevent data leakage between users
 *
 * @example
 * ```ts
 * const { actions } = useFollowsStore();
 * await actions.loadFollowedBrands();
 * await actions.optimisticallyFollowBrand(brandId, brand);
 * ```
 */
export const useFollowsStore = create(
  persist(
    combine(initialState, (set, get) => {
      const actions = {
        /** Sets the list of followed brands */
        setFollowedBrands: (brands: BrandFilter[]) => {
          set({ followedBrands: brands });
        },

        /** Sets the list of followed vendors */
        setFollowedVendors: (vendors: Vendor[]) => {
          set({ followedVendors: vendors });
        },

        /** Sets the loading state */
        setLoading: (loading: boolean) => set({ loading }),

        /**
         * Loads all followed brands for the authenticated user.
         * Uses cached data from localStorage if available.
         * Clears data and returns empty if user is not authenticated.
         */
        loadFollowedBrands: async () => {
          const token = await tokenService.getToken();
          if (!token) {
            set({ followedBrands: [], hasLoaded: true });
            return;
          }

          // Don't set loading to true if we already have data from localStorage
          const currentBrands = get().followedBrands;
          if (currentBrands.length === 0) {
            set({ loading: true });
          }

          try {
            const result = await followsApi.getFollowedBrands(token);

            if (result.success && result.data?.brands) {
              set({ followedBrands: result.data.brands, hasLoaded: true });
            } else {
              logError(new Error(result.error || "Failed to load followed brands"), {
                context: "loadFollowedBrands",
              });
              set({ followedBrands: [], hasLoaded: true });
            }
          } catch (error) {
            logError(error as Error, { context: "loadFollowedBrands" });
            set({ followedBrands: [], hasLoaded: true });
          } finally {
            set({ loading: false });
          }
        },

        /**
         * Loads all followed vendors for the authenticated user.
         * Always fetches from API (no cache).
         */
        loadFollowedVendors: async () => {
          const token = await tokenService.getToken();
          if (!token) {
            set({ followedVendors: [], hasLoaded: true });
            return;
          }

          set({ loading: true });

          try {
            const result = await followsApi.getFollowedVendors(token);
            if (result.success && result.data?.vendors) {
              set({ followedVendors: result.data.vendors, hasLoaded: true });
            } else {
              set({ hasLoaded: true });
            }
          } catch (error) {
            logError(error as Error, { context: "loadFollowedVendors" });
            set({ hasLoaded: true });
          } finally {
            set({ loading: false });
          }
        },

        /**
         * Checks for unsynced filters from followed brands/vendors.
         * Results are cached for 5 minutes. Use force=true to bypass cache.
         *
         * @param force - Whether to bypass cache and force a check
         */
        checkUnsyncedFilters: async (force = false) => {
          const { unsyncedFilters } = get();
          const now = Date.now();

          // Use cache if it's fresh and not forced
          if (
            !force &&
            unsyncedFilters.lastChecked &&
            now - unsyncedFilters.lastChecked < CACHE_DURATION
          ) {
            return;
          }

          set((state) => ({
            unsyncedFilters: { ...state.unsyncedFilters, checking: true },
          }));

          try {
            const token = await tokenService.getToken();
            if (!token) {
              set((state) => ({
                unsyncedFilters: { ...state.unsyncedFilters, checking: false },
              }));
              return;
            }

            const response = await followsApi.checkUnsyncedFilters(token);

            if (response.success && response.data) {
              set({
                unsyncedFilters: {
                  hasUnsynced: response.data.hasUnsynced,
                  unsyncedBrandCount: response.data.unsyncedBrandCount,
                  unsyncedVendorCount: response.data.unsyncedVendorCount,
                  lastChecked: now,
                  checking: false,
                },
              });
            } else {
              set((state) => ({
                unsyncedFilters: { ...state.unsyncedFilters, checking: false },
              }));
            }
          } catch (err) {
            logError(err as Error, { context: "checkUnsyncedFilters" });
            set((state) => ({
              unsyncedFilters: { ...state.unsyncedFilters, checking: false },
            }));
          }
        },

        /**
         * Invalidates the unsynced filters cache.
         * Call this after follow/unfollow operations to trigger a fresh check.
         */
        invalidateUnsyncedCache: () => {
          set((state) => ({
            unsyncedFilters: { ...state.unsyncedFilters, lastChecked: null },
          }));
        },

        /**
         * Follows a brand with optimistic UI updates.
         * Updates UI immediately, then calls API. Reverts on failure.
         * Invalidates unsynced filters cache.
         *
         * @param brandId - The brand ID to follow
         * @param brand - Full brand object (required for optimistic update)
         * @returns Promise resolving to success status and optional error message
         */
        optimisticallyFollowBrand: async (brandId: number, brand?: BrandFilter) => {
          const { followedBrands } = get();

          if (followedBrands.some((b) => b.id === brandId)) {
            return { success: true };
          }

          // Optimistically add - need the brand object
          if (!brand) {
            logError(new Error("Brand object required for optimistic update"), {
              context: "optimisticallyFollowBrand",
            });
            return { success: false, error: "Brand object required" };
          }

          const newBrands = [...followedBrands, brand];
          set({ followedBrands: newBrands });

          // Invalidate unsynced cache since follows changed
          actions.invalidateUnsyncedCache();

          try {
            const token = await tokenService.getToken();
            if (!token) {
              // Revert on error
              set({ followedBrands });
              return { success: false, error: "Not authenticated" };
            }

            const result = await followsApi.followBrand(token, brandId);

            if (!result.success) {
              // Revert on error
              set({ followedBrands });
              return { success: false, error: result.error };
            }

            return { success: true };
          } catch (error) {
            // Revert on error
            set({ followedBrands });
            return { success: false, error: (error as Error).message };
          }
        },

        /**
         * Unfollows a brand with optimistic UI updates.
         * Updates UI immediately, then calls API. Reverts on failure.
         * Invalidates unsynced filters cache.
         *
         * @param brandId - The brand ID to unfollow
         * @returns Promise resolving to success status and optional error message
         */
        optimisticallyUnfollowBrand: async (brandId: number) => {
          const { followedBrands } = get();

          const brandExists = followedBrands.some((b) => b.id === brandId);
          if (!brandExists) {
            return { success: true };
          }

          // Optimistically remove
          const newBrands = followedBrands.filter((b) => b.id !== brandId);
          set({ followedBrands: newBrands });

          // Invalidate unsynced cache since follows changed
          actions.invalidateUnsyncedCache();

          try {
            const token = await tokenService.getToken();
            if (!token) {
              // Revert on error
              set({ followedBrands });
              return { success: false, error: "Not authenticated" };
            }

            const result = await followsApi.unfollowBrand(token, brandId);

            if (!result.success) {
              // Revert on error
              set({ followedBrands });
              return { success: false, error: result.error };
            }

            return { success: true };
          } catch (error) {
            // Revert on error
            set({ followedBrands });
            return { success: false, error: (error as Error).message };
          }
        },

        /**
         * Follows a vendor with optimistic UI updates.
         * Updates UI immediately, then calls API. Reverts on failure.
         * Invalidates unsynced filters cache.
         *
         * @param vendorId - The vendor ID to follow
         * @param vendor - Full vendor object (required for optimistic update)
         * @returns Promise resolving to success status and optional error message
         */
        optimisticallyFollowVendor: async (vendorId: number, vendor?: Vendor) => {
          const { followedVendors } = get();

          if (followedVendors.some((v) => v.id === vendorId)) {
            return { success: true };
          }

          // Optimistically add - need the vendor object
          if (!vendor) {
            logError(new Error("Vendor object required for optimistic update"), {
              context: "optimisticallyFollowVendor",
            });
            return { success: false, error: "Vendor object required" };
          }

          const newVendors = [...followedVendors, vendor];
          set({ followedVendors: newVendors });

          // Invalidate unsynced cache since follows changed
          actions.invalidateUnsyncedCache();

          try {
            const token = await tokenService.getToken();
            if (!token) {
              // Revert on error
              set({ followedVendors });
              return { success: false, error: "Not authenticated" };
            }

            const result = await followsApi.followVendor(token, vendorId);

            if (!result.success) {
              // Revert on error
              set({ followedVendors });
              return { success: false, error: result.error };
            }

            return { success: true };
          } catch (error) {
            // Revert on error
            set({ followedVendors });
            return { success: false, error: (error as Error).message };
          }
        },

        /**
         * Unfollows a vendor with optimistic UI updates.
         * Updates UI immediately, then calls API. Reverts on failure.
         * Invalidates unsynced filters cache.
         *
         * @param vendorId - The vendor ID to unfollow
         * @returns Promise resolving to success status and optional error message
         */
        optimisticallyUnfollowVendor: async (vendorId: number) => {
          const { followedVendors } = get();

          const vendorExists = followedVendors.some((v) => v.id === vendorId);
          if (!vendorExists) {
            return { success: true };
          }

          // Optimistically remove
          const newVendors = followedVendors.filter((v) => v.id !== vendorId);
          set({ followedVendors: newVendors });

          // Invalidate unsynced cache since follows changed
          actions.invalidateUnsyncedCache();

          try {
            const token = await tokenService.getToken();
            if (!token) {
              // Revert on error
              set({ followedVendors });
              return { success: false, error: "Not authenticated" };
            }

            const result = await followsApi.unfollowVendor(token, vendorId);

            if (!result.success) {
              // Revert on error
              set({ followedVendors });
              return { success: false, error: result.error };
            }

            return { success: true };
          } catch (error) {
            // Revert on error
            set({ followedVendors });
            return { success: false, error: (error as Error).message };
          }
        },

        /**
         * Sets the current user ID and clears data if user changed.
         * Clears all follows and unsynced cache when switching users.
         *
         * @param userId - The new user ID, or null to logout
         */
        setCurrentUserId: (userId: string | null) => {
          const previousUserId = get().currentUserId;

          if (previousUserId !== userId) {
            // User changed, clear follows and unsynced cache
            set({
              followedBrands: [],
              followedVendors: [],
              currentUserId: userId,
              unsyncedFilters: {
                hasUnsynced: false,
                unsyncedBrandCount: 0,
                unsyncedVendorCount: 0,
                lastChecked: null,
                checking: false,
              },
            });
          }
        },

        /** Resets the store to initial state */
        reset: () => {
          set({
            followedBrands: [],
            followedVendors: [],
            loading: false,
            currentUserId: null,
            hasLoaded: false,
            unsyncedFilters: {
              hasUnsynced: false,
              unsyncedBrandCount: 0,
              unsyncedVendorCount: 0,
              lastChecked: null,
              checking: false,
            },
          });
        },
      };

      return { actions };
    }),
    {
      name: getUserStorageKey(null),
      storage: createJSONStorage(() => safeLocalStorage),
      partialize: (state) => ({
        followedBrands: state.followedBrands,
        followedVendors: state.followedVendors,
        currentUserId: state.currentUserId,
        unsyncedFilters: state.unsyncedFilters, // Persist cache
      }),
    }
  )
);
